import { current } from 'immer';
import { sdkApi } from '../api';
import { tasksSlice } from './tasksSlice';
import TCClient from 'client';
import type TasksService from 'js-sdk/src/services/TasksService';
import type { CreateTaskPayload, Task, UpdateTaskPayload } from 'js-sdk/src/types/Tasks';
import type { SDKQueryErrorType } from 'types/api';

const { setLinkedTask, setTasks } = tasksSlice.actions;

type TasksFilter = 'open' | 'completed';
type GetTasksArg = { organizationId: string; filter: TasksFilter };

function getTasksClient(): TasksService {
  return (TCClient as unknown as { tasks: TasksService }).tasks;
}

function toSdkQueryError(error: unknown): SDKQueryErrorType {
  return { reason: error instanceof Error ? error.message : String(error), code: 0 };
}

const tasksApi = sdkApi.injectEndpoints({
  endpoints: (build) => ({
    createTask: build.mutation<Task, { organizationId: string; payload: CreateTaskPayload }>({
      queryFn: async ({ organizationId, payload }) => {
        try {
          const data = await getTasksClient().create(organizationId, payload);
          return { data };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        }
      },
      async onQueryStarted({ organizationId }, { dispatch, queryFulfilled }) {
        try {
          const { data: task } = await queryFulfilled;
          dispatch(setLinkedTask(task));
          dispatch(
            tasksApi.util.updateQueryData(
              'getTasks',
              { organizationId, filter: 'open' },
              (draft) => {
                draft.push(task);
              }
            )
          );
        } catch {
          // Failure is surfaced to the modal via the mutation's error state.
        }
      },
    }),
    completeTask: build.mutation<Task, { organizationId: string; taskId: string }>({
      queryFn: async ({ organizationId, taskId }) => {
        try {
          const data = await getTasksClient().complete(organizationId, taskId);
          return { data };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        }
      },
      async onQueryStarted({ organizationId, taskId }, { dispatch, queryFulfilled }) {
        let movedTask: Task | undefined;
        const openPatch = dispatch(
          tasksApi.util.updateQueryData('getTasks', { organizationId, filter: 'open' }, (draft) => {
            const index = draft.findIndex((t) => t.id === taskId);
            if (index !== -1) {
              const [removed] = draft.splice(index, 1);
              movedTask = {
                ...current(removed),
                status: 'completed',
                completed_at: new Date().toISOString(),
              };
            }
          })
        );
        const completedPatch = dispatch(
          tasksApi.util.updateQueryData(
            'getTasks',
            { organizationId, filter: 'completed' },
            (draft) => {
              if (movedTask) draft.push(movedTask);
            }
          )
        );
        try {
          const { data: task } = await queryFulfilled;
          dispatch(setLinkedTask(task));
        } catch {
          openPatch.undo();
          completedPatch.undo();
        }
      },
    }),
    updateTask: build.mutation<
      Task,
      { organizationId: string; taskId: string; payload: UpdateTaskPayload }
    >({
      queryFn: async ({ organizationId, taskId, payload }) => {
        try {
          const data = await getTasksClient().update(organizationId, taskId, payload);
          return { data };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        }
      },
      async onQueryStarted({ organizationId, taskId }, { dispatch, queryFulfilled }) {
        try {
          const { data: task } = await queryFulfilled;
          dispatch(setLinkedTask(task));
          (['open', 'completed'] as const).forEach((filter) => {
            dispatch(
              tasksApi.util.updateQueryData('getTasks', { organizationId, filter }, (draft) => {
                const index = draft.findIndex((t) => t.id === taskId);
                if (index !== -1) draft[index] = task;
              })
            );
          });
        } catch {
          // Failure is surfaced via the mutation's error state.
        }
      },
    }),
    reopenTask: build.mutation<Task, { organizationId: string; taskId: string }>({
      queryFn: async ({ organizationId, taskId }) => {
        try {
          const data = await getTasksClient().reopen(organizationId, taskId);
          return { data };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        }
      },
      async onQueryStarted({ organizationId, taskId }, { dispatch, queryFulfilled }) {
        let movedTask: Task | undefined;
        const completedPatch = dispatch(
          tasksApi.util.updateQueryData(
            'getTasks',
            { organizationId, filter: 'completed' },
            (draft) => {
              const index = draft.findIndex((t) => t.id === taskId);
              if (index !== -1) {
                const [removed] = draft.splice(index, 1);
                movedTask = { ...current(removed), status: 'open', completed_at: null };
              }
            }
          )
        );
        const openPatch = dispatch(
          tasksApi.util.updateQueryData('getTasks', { organizationId, filter: 'open' }, (draft) => {
            if (movedTask) draft.push(movedTask);
          })
        );
        try {
          const { data: task } = await queryFulfilled;
          dispatch(setLinkedTask(task));
        } catch {
          completedPatch.undo();
          openPatch.undo();
        }
      },
    }),
    getTasks: build.query<Task[], GetTasksArg>({
      queryFn: async ({ organizationId, filter }) => {
        try {
          const data = await getTasksClient().list(organizationId, filter);
          return { data };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        }
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: tasks } = await queryFulfilled;
          dispatch(setTasks(tasks));
        } catch {
          // no-op — surfaced via query error state.
        }
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useCreateTaskMutation,
  useCompleteTaskMutation,
  useReopenTaskMutation,
  useUpdateTaskMutation,
  useGetTasksQuery,
} = tasksApi;

export { tasksApi };
